<script setup>
import { useGlobalStore } from 'src/composables/useGlobalStore'
import { inject, ref, watch } from 'vue'
import { useValidations } from '#/general/useValidations'
import registrationMutation from 'src/graphql/registrationMutation.gql'
import { useQuery, useMutation } from '@vue/apollo-composable'
import verifyEmailQuery from 'src/graphql/verifyEmailQuery.gql'
import reSendVerificationMutation from 'src/graphql/reSendVerificationMutation.gql'
import verifyTokenMutation from 'src/graphql/verifyTokenMutation.gql'
import saveUserInterestMutation from 'src/graphql/saveUserInterestMutation.gql'
import LoginForm from 'components/auth/LoginForm'
import SendPasswordResetEmail from 'components/auth/SendPasswordResetEmail'
import { useToast } from 'vue-toast-notification'
import AppDialog from '#/general/AppDialog'
import { useGA } from '#/useGA'
import { useShared } from '#/useShared'
import { useHubspot } from 'src/composables/useHubspot'
const $toast = useToast({ position: 'bottom' })
const { interpretErrorMessage } = useShared()

const { event } = useGA()
const { authModalVisible, refreshToken, authModalTab, newUser, performLogin } = useGlobalStore()
const { validateEmail, otpToken, required, nameValidation } = useValidations()
const { identify } = useHubspot()

const readTerms = ref(false)
const emailUnique = ref(true)
const verificationCode = ref(null)
const sentOnce = ref(false)
const lastHovered = ref(null)

watch(authModalVisible, val => {
  if (val === false) {
    authModalTab.value = 'LOGIN'
    newUser.value = {
      firstName: null,
      lastName: null,
      email: null,
      password: null,
      passwordAgain: null,
      interest: 'PRIVATE'
    }
  }
})

const { mutate: doRegistrationFirstStep, onError: onRegistrationFirstStepError } = useMutation(
  registrationMutation,
  () => ({
    update: (cache, { data }) => {
      if (data.registerFirstStep && data.registerFirstStep.success) {
        authModalTab.value = 'TOKEN'
      } else {
        $toast.open({ type: 'info', message: 'Hoppla - Hier ist etwas schiefgelaufen! Probiere es doch gleich nochmals oder melde dich bei den Raumpionieren, falls du weiterhin Probleme mit unserem Tool bekundest.' })
      }
    }
  })
)
onRegistrationFirstStepError(error => {
  console.log('REGISTRATION MUTATION ERROR', JSON.parse(JSON.stringify(error)))
  $toast.open({ type: 'warning', message: interpretErrorMessage(error) })
})

const { mutate: saveUserInterest, onError: onSaveUserInterestError } = useMutation(
  saveUserInterestMutation,
  () => ({
    update: (cache, { data }) => {
      if (data.updateUserInterest && data.updateUserInterest.success) {
        authModalVisible.value = false
        authModalTab.value = 'LOGIN'
        identify({
          id: data.updateUserInterest.b2bUserId,
          offer_interest: newUser.value.interest,
          email: newUser.value.email,
          firstname: newUser.value.firstName,
          lastname: newUser.value.lastName,
          user_persona: 'B2B'
        })
        newUser.value = {
          firstName: null,
          lastName: null,
          email: null,
          password: null,
          passwordAgain: null,
          interest: 'PRIVATE'
        }
        $toast.open({ type: 'info', message: 'Thank you for registering. We already logged you in.' })
      } else {
        $toast.open({ type: 'info', message: 'Hoppla - Hier ist etwas schiefgelaufen! Probiere es doch gleich nochmals oder melde dich bei den Raumpionieren, falls du weiterhin Probleme mit unserem Tool bekundest.' })
      }
    }
  })
)
onSaveUserInterestError(error => {
  console.log('saveUserInterest ERROR', JSON.parse(JSON.stringify(error)))
  $toast.open({ type: 'info', message: error.message })
})

const { mutate: verifyToken, onError: onVerifyTokenError } = useMutation(
  verifyTokenMutation,
  () => ({
    update: (cache, { data }) => {
      if (data.verifyRegistrationToken && data.verifyRegistrationToken.success) {
        authModalTab.value = 'LAST'
        event('b2b_conversion_1', { eventCategory: 'B2B', eventLabel: 'Registration - token confirmed' })
        performLogin({ email: newUser.value.email, password: newUser.value.password })
      } else if (data.verifyRegistrationToken && data.verifyRegistrationToken.success === false) {
        $toast.open({ type: 'info', message: 'The token you entered is incorrect, please try again.' })
      } else {
        $toast.open({ type: 'info', message: 'Hoppla - Hier ist etwas schiefgelaufen! Probiere es doch gleich nochmals oder melde dich bei den Raumpionieren, falls du weiterhin Probleme mit unserem Tool bekundest.' })
      }
    }
  })
)
onVerifyTokenError(error => {
  console.log('TOKEN VERIFICATION ERROR', JSON.parse(JSON.stringify(error)))
  $toast.open({ type: 'info', message: error.message })
})

const { mutate: reSendVerificationEmail, onError: onResendVerificationEmailError } = useMutation(
  reSendVerificationMutation,
  () => ({
    update: () => {
      sentOnce.value = true
      $toast.open({ type: 'positive', message: 'We have just sent a new verification code to your email address.' })
    }
  })
)
onResendVerificationEmailError(error => {
  console.log('RE SEND VERIFICAITON MUTATION ERROR', JSON.parse(JSON.stringify(error)))
  $toast.open({ type: 'info', message: error.message })
})

const { onResult: onEmailVerificationResult, onError: onEmailVerificationError } = useQuery(
  verifyEmailQuery,
  () => { return { email: newUser.value.email } },
  () => { return { enabled: !!newUser.value.email && authModalVisible.value, metadata: false } }
)
onEmailVerificationError(error => { console.log('EMAIL VERIFICATION ERROR', JSON.parse(JSON.stringify(error))) })
onEmailVerificationResult(result => {
  if (result && result.data && result.data.verifyEmail) {
    emailUnique.value = result.data.verifyEmail.isUnique
  }
})

const doRegister = () => {
  doRegistrationFirstStep({
    registrationFirstStepInput: {
      firstName: newUser.value.firstName,
      lastName: newUser.value.lastName,
      email: newUser.value.email,
      password: newUser.value.password
    }
  })
}

const doVerifyToken = () => {
  verifyToken({
    email: newUser.value.email,
    token: verificationCode.value
  })
}
function validatePasswordMatch () {
  if (newUser.value.password !== newUser.value.passwordAgain) { return 'The passwords you entered do not match.' }
}
function isEmailUnique () {
  if (authModalVisible.value && emailUnique.value === false) { return 'This email address is already taken.' }
}

</script>

<template lang="pug">
app-dialog(v-model:show-modal="authModalVisible" maximized full-screen persistent :fixed="true")
  template(#modal-content)
    .outerContainer
      .authModalContainer
        .absolute-top-right
          q-icon(name="fas fa-times" size="sm" @click="authModalVisible = false" color="grey" style="z-index: 1").q-mr-md.cursor-pointer.q-mt-md
            q-tooltip Close
        q-tab-panels(v-model='authModalTab' animated).q-pa-none.transparent
          q-tab-panel(name='LOGIN').q-pa-none
            login-form
          q-tab-panel(name='FORGOTTEN_PASSWORD').q-pa-none
            send-password-reset-email
          q-tab-panel(name='REGISTRATION').q-pa-none
            .full-width
              .mainTitle Registrieren
              q-form(@submit="doRegister" ref="form" style="margin-top: 40px;").full-width.row.q-mb-md.q-px-md
                .col-6.q-pr-md
                  q-input.appInput(v-model="newUser.firstName" hide-bottom-space borderless autocomplete="firstName" dark label="Name" color="white" :rules='[required, nameValidation]')
                .col-6.q-pl-md
                  q-input.appInput(v-model="newUser.lastName" hide-bottom-space borderless autocomplete="lastName" dark label="Vorname" color="white"  :rules='[required, nameValidation]')
                .col-12
                  q-input.appInput(v-model="newUser.email" hide-bottom-space borderless autocomplete="e-mail" dark label="E-mail" color="white"  :rules='[required, validateEmail, isEmailUnique]')
                .col-12
                  q-input.appInput(v-model="newUser.password" hide-bottom-space borderless autocomplete="password" dark label="Passwort" color="white" type="password"  :rules='[required]')
                .col-12
                  q-input.appInput(v-model="newUser.passwordAgain" hide-bottom-space borderless autocomplete="password-repeat" dark label="Passwort wiederholen" color="white" type="password"  :rules='[required, validatePasswordMatch]')
                .col-12.q-mt-sm
                  q-field(v-model='readTerms' :rules='[required]' borderless hide-bottom-space)
                    q-checkbox.q-mr-xs(v-model='readTerms' size='40px' dark color='brandGreen')
                    .read-terms--label Ich akzeptiere die RAUMPIONIERE &#32;
                      a.inline-block.link(href="https://raumpioniere.ch/terms-and-conditions" target="_blank") AGB &#32;
                      .inline-block und &#32;
                      a.inline-block.link(href="https://raumpioniere.ch/privacy-policy" target="_blank") Datenschutzerklärung
                      .inline-block .
                .full-width.q-my-md
                  q-btn(type="submit").registrationBtn Verifizierungscode senden
                .full-width.column.flex.items-center.q-mb-md
                  .row.q-mt-md(@click="authModalTab = 'LOGIN'")
                    .forgotText.cursor-pointer Bereits Registriert?
                    .registerText.cursor-pointer.q-ml-md Sign In
          q-tab-panel(name='TOKEN').q-pa-none.transparent
            .full-width
              .mainTitle Registrieren
              .tokenContainer
                .q-px-md.descriptionText Wir haben Ihnen den Verifizierungscode gesendet. Schauen Sie in Ihrrem Postfach nach und kopieren Sie den Code.
                q-form(@submit="doVerifyToken" ref="form" style="margin-top: 40px;").full-width.row.q-mb-md.q-px-md
                  .col-12.q-pr-md
                    q-input.appInput(disable v-model="newUser.email" hide-bottom-space borderless dark label="Name" color="white" :rules='[required]')
                  .col-12.q-pr-md
                    q-input.appInput(v-model="verificationCode" hide-bottom-space borderless dark label="Verifizierungscode" color="white" :rules='[required, otpToken]')
                  .full-width.q-mt-xl
                    q-btn(type="submit").registrationBtn Code verfizieren
                  .full-width.q-mt-lg
                    q-btn(@click="reSendVerificationEmail({ email: newUser.email })" :disable="sentOnce" style='backdrop-filter: blur(20px); background: linear-gradient(325.28deg, rgba(95, 239, 192, 0.168) -7.64%, rgba(95, 239, 192, 0.48) 102.78%);').registrationBtn Einen neuen Code anfordern
                    q-tooltip(v-if='sentOnce') You have already sent a verification email, check your spam folder or try again later.
                .full-width.column.flex.items-center.q-mb-md
                  .row(@click="authModalTab = 'LOGIN'")
                    .forgotText.cursor-pointer Bereits Registriert?
                    .registerText.cursor-pointer.q-ml-md Sign In
          q-tab-panel(name='LAST')
            .mainTitle Vielen Dank!
            .lastContainer
              .descriptionText Du hast dich soeben erfolgreich registriert - vielen Dank.
              .descriptionText.q-mt-md Bevor es losgeht, haben wir noch eine Frage: welche, der unten aufgeführten Rollen würde dich am besten Beschreiben?
              .row.full-width.q-mt-lg
                .col-6.q-pr-xs.q-pb-sm
                  .greenBox(@click="newUser.interest = 'PRIVATE'; saveUserInterest({ interest: newUser.interest })" @mouseleave="lastHovered = null" @mouseover="lastHovered = 'PRIVATE'" :class="(newUser.interest === 'PRIVATE' || lastHovered === 'PRIVATE') ? 'activeGreenBox' : ''")
                    .title Privatperson
                .col-6.q-pl-sm.q-pb-sm
                  .greenBox(@click="newUser.interest = 'DEVELOPMENT'; saveUserInterest({ interest: newUser.interest })" @mouseleave="lastHovered = null" @mouseover="lastHovered = 'DEVELOPMENT'" :class="(newUser.interest === 'DEVELOPMENT' || lastHovered === 'DEVELOPMENT') ? 'activeGreenBox' : ''")
                    .title
                      div Institutionelle(r)
                      div Immobilien-
                      div BesitzerIn oder
                      div InvestorIn
                .col-6.q-pr-sm.q-pt-sm
                  .greenBox.flex.flex-center(@click="newUser.interest = 'ARCHITECT'; saveUserInterest({ interest: newUser.interest })" @mouseleave="lastHovered = null" @mouseover="lastHovered = 'ARCHITECT'" :class="(newUser.interest === 'ARCHITECT' || lastHovered === 'ARCHITECT') ? 'activeGreenBox' : ''")
                    .title DienstleisterIn, PlanerIn oder UnternehmerIn innerhalb der Immobilienbranche
                .col-6.q-pl-sm.q-pt-sm
                  .greenBox.flex.flex-center(@click="newUser.interest = 'GOVERMENT'; saveUserInterest({ interest: newUser.interest })" @mouseleave="lastHovered = null" @mouseover="lastHovered = 'GOVERMENT'" :class="(newUser.interest === 'GOVERMENT' || lastHovered === 'GOVERMENT') ? 'activeGreenBox' : ''")
                    .title Andere
</template>

<style lang="sass">
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
textarea:-webkit-autofill,
textarea:-webkit-autofill:hover,
textarea:-webkit-autofill:focus,
select:-webkit-autofill,
select:-webkit-autofill:hover,
select:-webkit-autofill:focus
  -webkit-text-fill-color: white
  -webkit-box-shadow: 0 0 0 1000px transparent inset !important
  transition: background-color 300ms ease-in-out 0s
.q-field--outlined .q-field__control:before
  z-index: 1

.outerContainer
  width: 100%
  height: 100%
  display: flex
  @media screen and (min-width: 1024px)
    justify-content: center
    align-items: center
    margin-top: 90px
    height: calc(100% - 90px)
  @media screen and (max-width: 1023px)
    margin-top: 80px
    height: calc(100% - 80px)
  .authModalContainer
    position: relative
    background: linear-gradient(152.99deg, #2F3849 78.27%, #21252E 157.29%)
    box-shadow: 0px 15px 30px rgba(166, 166, 166, 0.25)
    width: 520px
    @media screen and (min-width: 1024px)
      max-height: 80vh
      border-radius: 10px
      padding: 45px 30px 30px 30px
      max-width: 90%
      margin-bottom: 90px
    @media screen and (max-width: 1023px)
      max-width: 100%
      padding: 20px 16px 16px 16px
    .forgotText
      font-size: 16px
      font-style: normal
      font-weight: 400
      line-height: 21px
      letter-spacing: 0em
      text-align: left
      color: #9D9DD5
    .read-terms--label
      font-weight: 500
      font-size: 16px
      line-height: 130%
      color: #9D9DD5
      align-self: center
      .link
        text-decoration: none
        cursor: pointer
        color: #5FEFC0
    .mainTitle
      font-size: 24px
      font-style: normal
      font-weight: 700
      line-height: 31px
      letter-spacing: 0
      text-align: center
      color: white
    .registrationBtn
      display: flex
      flex-direction: row
      justify-content: center
      align-items: center
      padding: 12px 28px
      height: 54px
      width: 100%
      background: #5FEFC0
      border-radius: 10px
      font-size: 20px
      font-style: normal
      font-weight: 600
      line-height: 30px
      letter-spacing: 0em
      text-align: left
      text-transform: none
    .registerText
      font-size: 16px
      font-style: normal
      font-weight: 700
      line-height: 21px
      letter-spacing: 0em
      text-align: center
      color: #5FEFC0
    .appInput
      input
        font-size: 16px !important
        line-height: 130% !important
        color: white !important
      .q-field__control:before
        border-bottom: 1px solid #5C5C73
      .q-field__label
        font-weight: 600
        font-size: 16px
        line-height: 130%
        color: #9D9DD5
    .q-checkbox__label
      font-weight: 500
      font-size: 16px
      line-height: 130%
      color: #9D9DD5
    .q-field__label
      color: #5FEFC0 !important
      font-size: 14px !important
    .descriptionText
      font-weight: 500
      font-size: 16px
      line-height: 130%
      color: #9D9DD5
    .tokenContainer
      padding: 50px 0
    .lastContainer
      padding: 30px 0 0px 0
    .activeGreenBox
      background: rgba(95, 239, 192, 1) !important
    .greenBox
      cursor: pointer
      width: 100%
      background: #5FEFC0
      height: 173px
      background: linear-gradient(325.28deg, rgba(95, 239, 192, 0.168) -7.64%, rgba(95, 239, 192, 0.48) 102.78%)
      backdrop-filter: blur(40px)
      border-radius: 10px
      padding: 20px
      display: flex
      justify-content: center
      align-items: center
      .title
        font-weight: bold
        font-size: 14px
        line-height: 154%
        color: #22223C
    .socialBtn
      width: 54px
      cursor: pointer
      height: 54px
      margin: 0 10px
      background: linear-gradient(146.58deg, rgba(98, 108, 133, 0.6) 2.34%, rgba(98, 108, 133, 0.1) 105.64%)
      backdrop-filter: blur(40px)
      border-radius: 6px
      display: flex
      justify-content: center
      align-items: center
</style>
